import { useConfirmationModal } from '@bridebook/aura';
import useCopyToClipboard from '@bridebook/ui/src/utils/hooks/use-copy-to-clipboard';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { toggleSnackbar } from '@bridebook/web/lib/bbcommon/actions';
import { env } from '@bridebook/web/lib/env';
import { sharedGuestCollectorLinkAnalytics } from '@bridebook/web/lib/guestlist/analytics/actions';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { useDispatch, useSelector } from '@bridebook/web/lib/utils';
import { getWeddingId } from '@bridebook/web/lib/weddings/selectors';
import { useTranslation } from 'next-i18next';
import { useCallback } from 'react';

type IArgs =
  | {
      collectGuestInfoPopupLocation: 'guestComms';
      section: 'messages' | 'selectRecipients';
    }
  | {
      collectGuestInfoPopupLocation: 'guestList';
    };
export const useShareAddressCollectorUrlModal = (args: IArgs) => {
  const { t } = useTranslation('inbox');
  const dispatch = useDispatch();
  const { openModal: openInterceptModal } = useConfirmationModal();

  const weddingId = useSelector(getWeddingId);
  const { copy } = useCopyToClipboard();
  const market = useMarket();
  const isLive = env.LIVE;

  const origin = isLive
    ? 'https://bridebook.com'
    : typeof window !== 'undefined'
      ? window.location.origin
      : env.COUPLESIDE_URL;
  const basePath = `${market.prefix}`;

  const addressCollectorUrl = `${origin}/${basePath}${UrlHelper.addressCollector.guestForm(weddingId)}`;

  const handleCopyLink = useCallback(async () => {
    dispatch(
      sharedGuestCollectorLinkAnalytics({
        url: addressCollectorUrl,
        collectGuestInfoPopupLocation: args.collectGuestInfoPopupLocation,
        section: args.collectGuestInfoPopupLocation === 'guestComms' ? args.section : undefined,
      }),
    );
    try {
      copy(addressCollectorUrl);
      dispatch(toggleSnackbar('success', t('guests.addressCollectorModal.linkCopied')));
    } catch (error) {
      dispatch(toggleSnackbar('alert', t('guests.addressCollectorModal.copyError')));
    }
  }, [dispatch, addressCollectorUrl, args, copy, t]);

  const handleOpenLinkInBrowser = useCallback(async () => {
    window.open(addressCollectorUrl, '_blank');
  }, [addressCollectorUrl]);

  return useCallback(() => {
    openInterceptModal({
      title: t('guests.addressCollectorModal.title'),
      text: t('guests.addressCollectorModal.description'),
      primaryButtonText: t('guests.addressCollectorModal.cta.copy'),
      onPrimaryButtonClick: handleCopyLink,
      secondaryButtonText: t('guests.addressCollectorModal.cta.open'),
      onSecondaryButtonClick: handleOpenLinkInBrowser,
      secondaryButtonProps: { variant: 'secondary' },
    });
  }, [handleCopyLink, handleOpenLinkInBrowser, openInterceptModal, t]);
};
